import React, { useMemo, useRef } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useAppSelector, useAppDispatch } from '../../ducks/hooks';
import { setDefaultFilters } from '../../ducks/filters/duck-filters';
import { useTrackInteraction } from '../../services/logging/hooks';
import { FilterPill, Icon, Text, Button } from '../../providers/harmony-scope/lazy-enablers';
import { useFiscalYearSelection, useRoleSelection, useTermSelection } from './hooks';
import { ETabId } from '../../services/tab-configurations/tab-types';
import './component-filter-pills.css';
import { EPartitionCadence } from '../../ducks/filters/types-filters';
import { getFormattedMonthYear } from '../../utils/date-utils';

const componentName: string = 'Filter Pills';

const useMonthlyPillGuard = (): boolean => {
  const activeTab = useAppSelector((state) => state.filters?.activeTab);
  const filters = useAppSelector((state) => state.filters);

  return useMemo(() => {
    if (activeTab !== ETabId.PerformanceTab && activeTab !== ETabId.MyCompensationTab && activeTab !== ETabId.MyPlanTab) return false;
    return filters?.selectedPerformancePartitionGroup?.partitionCadence === EPartitionCadence.Monthly;
  }, [activeTab, filters?.selectedPerformancePartitionGroup?.partitionCadence]);
};

export const FilterPills = () => {
  const { t } = useMEMTranslation();
  const trackInteraction = useTrackInteraction();
  const activeTab = useAppSelector((state) => state.filters?.activeTab);
  const filters = useAppSelector((state) => state.filters);
  const appConfig = useAppSelector((state) => state.appConfig);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const participationName = useRoleSelection();
  const term = useTermSelection();
  const fiscalYear = useFiscalYearSelection();
  const dispatch: any = useAppDispatch();
  const showMonthlyPill = useMonthlyPillGuard();

  const selectedMonth = useMemo(() => {
    if (filters?.selectedPerformancePartition?.title && filters?.selectedPerformancePartitionGroup?.partitionCadence === EPartitionCadence.Monthly) {
      return getFormattedMonthYear(filters.selectedPerformancePartition.title);
    }
    return filters?.selectedPerformancePartition?.title;
  }, [filters?.selectedPerformancePartition?.title, filters?.selectedPerformancePartitionGroup?.partitionCadence]);

  const ariaLiveRegionRef = useRef<HTMLDivElement>(null);
  const clearFilters = () => {
    if (filters && appConfig && participationDetails) {
      trackInteraction('Cleared filters', activeTab as string, componentName);
      dispatch(setDefaultFilters({ appConfig, participationDetails }));
      // Announce ARIA message for filters reseted successfully using ref
      if (ariaLiveRegionRef.current) {
        ariaLiveRegionRef.current.textContent = t('ariaFiltersResetSuccess');
        setTimeout(() => {
          if (ariaLiveRegionRef.current) {
            ariaLiveRegionRef.current.textContent = '';
          }
        }, 3000);
      }
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '.5em',
        flexWrap: 'wrap',
      }}
    >
      <Text appearance="paragraph">{`${t('filterPillsFilteredBy')}: `}</Text>
      <FilterPill>
        {`${t('filtersPanelFiscalYearLabel')}: `}
        <span slot="value">{fiscalYear}</span>
      </FilterPill>
      <FilterPill>
        {`${t('filtersPanelRoleLabel')}: `}
        <span slot="value">{participationName}</span>
      </FilterPill>
      <FilterPill>
        {`${t('filtersPanelTimePeriodLabel')}: `}
        <span slot="value">{term}</span>
      </FilterPill>
      {showMonthlyPill && selectedMonth && (
        <FilterPill>
          {`${t('filtersPillsMonthLabel')}: `}
          <span slot="value">{selectedMonth}</span>
        </FilterPill>
      )}
      <Button appearance="command" onClick={clearFilters}>
        <Icon slot="start" name="clearfilter" />
        {t('filtersPanelResetFilter')}
      </Button>
      {/* ARIA live region for filter reset success, now using ref */}
      <div
        id="FiltersAppliedAriaLiveRegion"
        ref={ariaLiveRegionRef}
        aria-live="polite"
        style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
      />
    </div>
  );
};
